/**
 * Simplified Feature Flags Service using Environment Variables
 * Simple boolean flags for Convex features - no complex migration phases
 */

export interface ConvexFeatureFlags {
  // Core Convex features
  'convex-enabled': boolean;
  'convex-auth-integration': boolean;
  'convex-user-sync': boolean;

  // Data features
  'convex-parallel-state': boolean;
  'convex-job-queries': boolean;
  'convex-preferences-sync': boolean;
  'convex-file-queue-sync': boolean;

  // Real-time features
  'convex-realtime-jobs': boolean;
  'convex-realtime-notifications': boolean;
  'convex-multi-tab-sync': boolean;

  // Rollback and safety
  'convex-fallback-to-localstorage': boolean;
  'convex-debug-logging': boolean;
}

class FeatureFlagsService {
  private readonly flags: ConvexFeatureFlags;

  constructor() {
    // Read environment variables at initialization
    const isEnabled = process.env.REACT_APP_CONVEX_ENABLED === 'true';
    const isDebugEnabled = process.env.REACT_APP_CONVEX_DEBUG === 'true';

    // Simple configuration: when Convex is enabled, all features are enabled
    this.flags = {
      // Master switch
      'convex-enabled': isEnabled,

      // Core features - all enabled when Convex is enabled
      'convex-auth-integration': isEnabled,
      'convex-user-sync': isEnabled,

      // Data features - all enabled when Convex is enabled
      'convex-parallel-state': isEnabled,
      'convex-job-queries': isEnabled,
      'convex-preferences-sync': isEnabled,
      'convex-file-queue-sync': isEnabled,

      // Real-time features - all enabled when Convex is enabled
      'convex-realtime-jobs': isEnabled,
      'convex-realtime-notifications': isEnabled,
      'convex-multi-tab-sync': isEnabled,

      // Safety features
      'convex-fallback-to-localstorage': true, // Always enabled for safety
      'convex-debug-logging': isDebugEnabled,
    };
  }

  /**
   * Initialize feature flags (now synchronous)
   */
  async initialize(): Promise<void> {
    // No-op - flags are set at construction time
    return Promise.resolve();
  }

  /**
   * Check if a feature flag is enabled
   */
  isEnabled(flag: keyof ConvexFeatureFlags): boolean {
    return this.flags[flag] ?? false;
  }

  /**
   * Get all current flag values
   */
  getAllFlags(): ConvexFeatureFlags {
    return { ...this.flags };
  }

  /**
   * Check if Convex is enabled at all
   */
  isConvexEnabled(): boolean {
    return this.flags['convex-enabled'];
  }

  /**
   * Check if we should fallback to localStorage
   */
  shouldFallbackToLocalStorage(): boolean {
    return this.flags['convex-fallback-to-localstorage'];
  }

  /**
   * Check if debug logging is enabled
   */
  isDebugEnabled(): boolean {
    return this.flags['convex-debug-logging'];
  }

  /**
   * Force refresh feature flags (no-op for environment variables)
   */
  async refresh(): Promise<void> {
    // No-op - environment variables don't change at runtime
    return Promise.resolve();
  }
}

// Export singleton instance
export const featureFlags = new FeatureFlagsService();

// Export for testing
export default featureFlags;